import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError, fmtINR } from "@/lib/api";
import { PageHeader, StatusBadge, DEPARTMENTS } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import OfferLetter from "@/components/OfferLetter";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FileText, Download, Loader2, Sparkles, CheckCircle2, Eye, Send, PenLine, AlertTriangle } from "lucide-react";
import { downloadLetterPdf } from "@/lib/pdf";
import { toast } from "sonner";

export default function Offers() {
  const qc = useQueryClient();
  const location = useLocation();
  const preset = location.state?.candidate;

  const [form, setForm] = useState(() => ({
    candidate_id: preset?.id || null,
    candidate_name: preset?.name || "",
    email: preset?.email || "",
    designation: preset?.role || "",
    department: preset?.department || "Engineering",
    division: "Domestic",
    annual_ctc: preset?.expected_ctc || 1200000,
    metro: true,
    joining_date: preset?.joining_date || "",
    work_location: "", reporting_manager: "", probation_months: "", notice_period: "", dob: "", additional_terms: "",
  }));
  const [manual, setManual] = useState(false);
  const [preview, setPreview] = useState(null);
  const [viewOffer, setViewOffer] = useState(null);
  const [tempCred, setTempCred] = useState(null);
  const [signFor, setSignFor] = useState(null);
  const [signForm, setSignForm] = useState({ signed_name: "", consent_nda: false, consent_bgv: false });

  const { data: offers = [] } = useQuery({
    queryKey: ["offers"], queryFn: async () => (await api.get("/offers")).data,
  });
  const { data: computed } = useQuery({
    queryKey: ["compute", form.annual_ctc, form.metro],
    queryFn: async () => (await api.post("/salary/compute", { annual_ctc: form.annual_ctc, metro: form.metro })).data,
    enabled: form.annual_ctc > 0,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/offers", { ...form, probation_months: form.probation_months === "" ? null : Number(form.probation_months) })).data,
    onSuccess: (data) => { qc.invalidateQueries({ queryKey: ["offers"] }); setPreview(data); toast.success(data.emailed ? "Offer generated & emailed to candidate" : "Offer generated (email not delivered — check candidate email)"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const bgvMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/offers/${id}/bgv`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["offers"] }); toast.success("BGV status updated"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const signMut = useMutation({
    mutationFn: async ({ id, body }) => (await api.post(`/offers/${id}/sign`, body)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["offers"] }); setSignFor(null); toast.success("Offer e-signed — NDA & BGV consent captured"); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const acceptMut = useMutation({
    mutationFn: async (id) => (await api.post(`/offers/${id}/accept`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["offers"] });
      qc.invalidateQueries({ queryKey: ["employees"] });
      if (data.temp_password) setTempCred({ name: data.name, email: data.email, temp_password: data.temp_password });
      toast.success("Offer accepted — employee onboarded!");
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const emailMut = useMutation({
    mutationFn: async (id) => (await api.post(`/offers/${id}/email`)).data,
    onSuccess: (d) => toast.success(`Offer letter emailed to ${d.to}`),
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const incompleteSalary = (o) => !o.breakdown || !o.breakdown.earnings || !o.breakdown.deductions || !o.breakdown.monthly;

  return (
    <div>
      <PageHeader title="Offers & Onboarding" subtitle="Auto-generate India-compliant offer letters and onboard hires." />

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form + live breakdown */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="p-5 border border-border rounded-xl">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-accent" /> Offer Details</h3>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Candidate Name</Label><Input data-testid="offer-name-input" value={form.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="offer-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Designation</Label><Input data-testid="offer-designation-input" value={form.designation} onChange={(e) => set("designation", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Department</Label>
                  <Select value={form.department} onValueChange={(v) => set("department", v)}>
                    <SelectTrigger data-testid="offer-department-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Annual CTC (₹)</Label><Input data-testid="offer-ctc-input" type="number" value={form.annual_ctc} onChange={(e) => set("annual_ctc", Number(e.target.value))} /></div>
              <div className="space-y-1.5"><Label>Division (Employee ID series)</Label>
                <Select value={form.division} onValueChange={(v) => set("division", v)}>
                  <SelectTrigger data-testid="offer-division-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Executive Leadership">Executive Leadership (SITS0001…)</SelectItem>
                    <SelectItem value="Human Resources">Human Resources (SITS0003…)</SelectItem>
                    <SelectItem value="Domestic">Domestic (SITS0004…)</SelectItem>
                    <SelectItem value="International">International (SITS0005…)</SelectItem>
                    <SelectItem value="Medical Billing">Medical Billing (SITS0006…)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Expected Joining</Label><Input data-testid="offer-joining-input" type="date" value={form.joining_date} onChange={(e) => set("joining_date", e.target.value)} /></div>
              <div className="flex items-center justify-between py-1">
                <Label className="cursor-pointer">Metro city (HRA 50%)</Label>
                <Switch checked={form.metro} onCheckedChange={(v) => set("metro", v)} data-testid="offer-metro-switch" />
              </div>
              <button type="button" onClick={() => setManual((m) => !m)} className="text-xs text-primary font-medium underline-offset-2 hover:underline" data-testid="offer-manual-toggle">
                {manual ? "Hide additional details" : "+ Fill additional details manually (location, manager, probation, notice, DOB, terms)"}
              </button>
              {manual && (
                <div className="space-y-3 rounded-lg border border-dashed border-border p-3" data-testid="offer-manual-fields">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>Work Location</Label><Input data-testid="offer-location-input" value={form.work_location} onChange={(e) => set("work_location", e.target.value)} placeholder="Visakhapatnam" /></div>
                    <div className="space-y-1.5"><Label>Reporting Manager</Label><Input data-testid="offer-manager-input" value={form.reporting_manager} onChange={(e) => set("reporting_manager", e.target.value)} /></div>
                    <div className="space-y-1.5"><Label>Probation (months)</Label><Input data-testid="offer-probation-input" type="number" min="0" value={form.probation_months} onChange={(e) => set("probation_months", e.target.value)} placeholder="6" /></div>
                    <div className="space-y-1.5"><Label>Notice Period</Label><Input data-testid="offer-notice-input" value={form.notice_period} onChange={(e) => set("notice_period", e.target.value)} placeholder="60 days" /></div>
                    <div className="space-y-1.5 col-span-2"><Label>Date of Birth</Label><Input data-testid="offer-dob-input" type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} /></div>
                  </div>
                  <div className="space-y-1.5"><Label>Additional Terms / Notes</Label>
                    <textarea data-testid="offer-terms-input" value={form.additional_terms} onChange={(e) => set("additional_terms", e.target.value)} rows={3} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="e.g. Joining bonus, shift timings, relocation support…" />
                  </div>
                </div>
              )}
              <Button
                className="w-full rounded-lg font-semibold active:scale-[0.98]"
                data-testid="generate-offer-button"
                disabled={createMut.isPending || !form.candidate_name || !form.email}
                onClick={() => createMut.mutate()}
              >
                {createMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-1.5" /> Generate Offer Letter</>}
              </Button>
            </div>
          </Card>

          {computed && (
            <Card className="p-5 border border-border rounded-xl" data-testid="live-breakdown">
              <h3 className="font-heading font-semibold mb-3 text-sm">Live Statutory Breakdown (Annual)</h3>
              <div className="space-y-1.5 text-sm">
                {[
                  ["Basic (50%)", computed.earnings.basic],
                  ["HRA", computed.earnings.hra],
                  ["Special Allowance", computed.earnings.special_allowance],
                  ["PF (Employer)", computed.employer_contributions.pf_employer],
                  ["Health Insurance", computed.employer_contributions.health_insurance],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-muted-foreground">{k}</span><span className="font-mono">{fmtINR(v)}</span></div>
                ))}
                <div className="flex justify-between border-t border-border pt-2 mt-2 font-semibold text-accent"><span>Net Take-Home / Month</span><span className="font-mono">{fmtINR(computed.net_monthly)}</span></div>
              </div>
            </Card>
          )}
        </div>

        {/* Offers list */}
        <div className="lg:col-span-3">
          <Card className="border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border"><h3 className="font-heading font-semibold">Generated Offers</h3></div>
            <div className="divide-y divide-border">
              {offers.length === 0 && <p className="text-sm text-muted-foreground text-center py-10">No offers yet. Generate one on the left.</p>}
              {offers.map((o) => (
                <div key={o.id} className="flex items-center gap-3 px-5 py-4 hover:bg-secondary/50 transition-colors" data-testid={`offer-row-${o.id}`}>
                  <div className="w-10 h-10 rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-950/50 dark:text-violet-400 flex items-center justify-center font-bold text-sm">
                    {o.candidate_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{o.candidate_name} {o.signed && <span className="text-[10px] font-mono text-emerald-600">✓ Signed</span>}{o.email_bounced && <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-destructive ml-1" data-testid={`offer-bounced-${o.id}`} title="The offer email bounced — resend it"><AlertTriangle className="w-3 h-3" /> Email bounced</span>}{incompleteSalary(o) && <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600 ml-1" data-testid={`offer-incomplete-${o.id}`} title="Salary data incomplete — regenerate this offer"><AlertTriangle className="w-3 h-3" /> Incomplete salary</span>}</p>
                    <p className="text-xs text-muted-foreground truncate">{o.designation} • {fmtINR(o.annual_ctc)} • {o.division || "Domestic"}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="text-[10px] text-muted-foreground">BGV:</span>
                      <Select value={o.bgv_status || "Initiated"} onValueChange={(v) => bgvMut.mutate({ id: o.id, status: v })}>
                        <SelectTrigger className="h-6 text-[10px] w-28" data-testid={`bgv-select-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{["Initiated", "In Progress", "Cleared", "Flagged"].map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <StatusBadge status={o.status} />
                  <Button size="sm" variant="ghost" onClick={() => setViewOffer(o)} data-testid={`view-offer-${o.id}`}><Eye className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => emailMut.mutate(o.id)} disabled={emailMut.isPending} data-testid={`email-offer-${o.id}`} title="Email offer to candidate"><Send className="w-4 h-4" /></Button>
                  {!o.signed && (
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => { setSignForm({ signed_name: o.candidate_name, consent_nda: false, consent_bgv: false }); setSignFor(o); }} data-testid={`sign-offer-${o.id}`}>
                      <PenLine className="w-4 h-4 mr-1" /> e-Sign
                    </Button>
                  )}
                  {o.status !== "Accepted" && (
                    <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => acceptMut.mutate(o.id)} disabled={acceptMut.isPending} data-testid={`accept-offer-${o.id}`}>
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Onboard
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Preview after generation & view dialog share the same modal */}
      <Dialog open={!!(preview || viewOffer)} onOpenChange={(o) => { if (!o) { setPreview(null); setViewOffer(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="font-heading flex items-center justify-between">
              Offer Letter
              <Button size="sm" variant="outline" className="rounded-lg mr-4" onClick={() => downloadLetterPdf(`Offer_${((preview || viewOffer)?.candidate_name || "Letter").replace(/\s+/g, "_")}.pdf`)} data-testid="offer-pdf-download-button">
                <Download className="w-4 h-4 mr-1.5" /> Download PDF
              </Button>
            </DialogTitle>
            <DialogDescription className="sr-only">India statutory salary breakdown offer letter preview.</DialogDescription>
          </DialogHeader>
          {(preview || viewOffer) && <ErrorBoundary><OfferLetter offer={preview || viewOffer} /></ErrorBoundary>}
        </DialogContent>
      </Dialog>

      {/* e-Sign NDA & BGV */}
      <Dialog open={!!signFor} onOpenChange={(o) => !o && setSignFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">e-Sign Offer — NDA &amp; BGV Consent</DialogTitle>
            <DialogDescription>Confirm acceptance of the Non-Disclosure Agreement and Background Verification consent.</DialogDescription>
          </DialogHeader>
          {signFor && (
            <div className="space-y-3" data-testid="esign-dialog">
              <div className="space-y-1.5"><Label>Full legal name</Label><Input value={signForm.signed_name} onChange={(e) => setSignForm((f) => ({ ...f, signed_name: e.target.value }))} data-testid="esign-name-input" /></div>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Switch checked={signForm.consent_nda} onCheckedChange={(v) => setSignForm((f) => ({ ...f, consent_nda: v }))} data-testid="esign-nda-switch" />
                <span>I have read and agree to the <strong>Non-Disclosure Agreement</strong> (Annexure B).</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Switch checked={signForm.consent_bgv} onCheckedChange={(v) => setSignForm((f) => ({ ...f, consent_bgv: v }))} data-testid="esign-bgv-switch" />
                <span>I consent to <strong>Background Verification</strong> (Annexure C) and confirm my details are true.</span>
              </label>
              <Button className="w-full rounded-lg font-semibold" disabled={signMut.isPending || !signForm.consent_nda || !signForm.consent_bgv || !signForm.signed_name.trim()} onClick={() => signMut.mutate({ id: signFor.id, body: signForm })} data-testid="esign-submit-button">
                {signMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign & Accept"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* One-time temp credentials shown to HR after onboarding */}
      <Dialog open={!!tempCred} onOpenChange={(o) => !o && setTempCred(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Employee Account Created</DialogTitle>
            <DialogDescription>Share these one-time credentials with the employee. They must set a new password on first sign-in.</DialogDescription>
          </DialogHeader>
          {tempCred && (
            <div className="space-y-3" data-testid="temp-credentials">
              <div className="rounded-lg border border-border bg-secondary p-4 space-y-2 text-sm">
                <p><span className="text-muted-foreground">Name:</span> <span className="font-semibold">{tempCred.name}</span></p>
                <p><span className="text-muted-foreground">Email:</span> <span className="font-mono">{tempCred.email}</span></p>
                <p><span className="text-muted-foreground">Temporary password:</span> <span className="font-mono font-bold" data-testid="temp-password-value">{tempCred.temp_password}</span></p>
              </div>
              <p className="text-xs text-muted-foreground">This password is shown only once. The employee will be forced to change it on first login.</p>
              <Button variant="outline" className="w-full rounded-lg" onClick={() => { navigator.clipboard.writeText(`${tempCred.email} / ${tempCred.temp_password}`); toast.success("Copied"); }} data-testid="copy-temp-cred-button">
                Copy Credentials
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
